import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  useCallback,
  useState,
} from 'react';

interface HighlightRange {
  start: number;
  end: number;
}

interface InputValueAndSelection {
  text: string;
  selection?: HighlightRange;
}

export enum NoraStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
}
export interface ThreadNoraState {
  status: NoraStatus;
  reason?: string;
}
type NoraState = {
  [threadId: string]: ThreadNoraState;
};

interface ChatUIContextType {
  setSuggestedChatMessage: (
    message: string,
    start?: number,
    end?: number,
  ) => void;
  inputValueAndSelection: InputValueAndSelection;
  setInputValueAndSelection: Dispatch<SetStateAction<InputValueAndSelection>>;
  noraState: NoraState;
  setNoraStateForThread: (threadId: string, state: ThreadNoraState) => void;
}

const defaultChatUIContext: ChatUIContextType = {
  setSuggestedChatMessage: () =>
    console.error('setSuggestedChatMessage not implemented'),
  inputValueAndSelection: { text: '', selection: undefined },
  setInputValueAndSelection: () =>
    console.error('setInputValue not implemented'),
  noraState: {},
  setNoraStateForThread: () =>
    console.error('setNoraStateForThread not implemented'),
};

export const ChatUIContext =
  createContext<ChatUIContextType>(defaultChatUIContext);

export const ChatUIContextProvider: FC<{
  children?: ReactElement | Array<ReactElement>;
}> = ({ children }) => {
  const [inputValueAndSelection, setInputValueAndSelection] =
    useState<InputValueAndSelection>({
      text: '',
      selection: undefined,
    });

  const [noraState, setNoraState] = useState<NoraState>({});

  const setNoraStateForThread = useCallback(
    (threadId: string, state: ThreadNoraState) => {
      setNoraState((current) => {
        return {
          ...current,
          ...{ [threadId]: state },
        };
      });
    },
    [setNoraState],
  );

  const setSuggestedChatMessage = useCallback(
    (message: string, start?: number, end?: number) => {
      if (start !== undefined && end !== undefined) {
        setInputValueAndSelection({ text: message, selection: { start, end } });
      } else {
        setInputValueAndSelection({ text: message, selection: undefined });
      }
    },
    [setInputValueAndSelection],
  );

  return (
    <ChatUIContext.Provider
      value={{
        setSuggestedChatMessage,
        inputValueAndSelection,
        setInputValueAndSelection,
        noraState,
        setNoraStateForThread,
      }}
    >
      {children}
    </ChatUIContext.Provider>
  );
};
